import { useEffect, useState } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  LaptopOutlined,
  PlusOutlined,
  SendOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Select, Switch, Tooltip, message } from 'antd';
import { EQUIPMENT_SETTINGS_QUERY_KEY } from '@/components/email-templates/constants';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import MailRecipientsField from '@/components/email-templates/MailRecipientsField';
import { mailRecipientsToForm } from '@/components/email-templates/mailRecipientsForm';
import { HowToCard } from '@/components/how-to-card';
import {
  getEquipmentSettings,
  updateEquipmentSettings,
} from '@/services/equipmentRequests.service';
import EquipmentImportModal from './EquipmentImportModal';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 10;
const MAX_SERVICES = 20;
const MAX_ITEMS = 300;
const MAX_GROUPS = 30;
const GUIDE_ICONS = [
  TeamOutlined,
  UnorderedListOutlined,
  FileExcelOutlined,
  UserSwitchOutlined,
  SendOutlined,
  HistoryOutlined,
];
const MODES = ['multi', 'single', 'yesNo'];
const MODE_KEYS = { multi: 'groupModeMulti', single: 'groupModeSingle', yesNo: 'groupModeYesNo' };
const ITEM_ROW =
  'grid grid-cols-[minmax(0,1fr)_minmax(0,170px)_auto_32px] items-center gap-2';
const ROW = 'grid grid-cols-[minmax(0,180px)_minmax(0,1fr)_32px] items-start gap-2';
const HEAD = 'grid grid-cols-[minmax(0,180px)_minmax(0,1fr)_32px] gap-2 text-[11.5px] font-semibold text-zinc-500';

const emailsSelectProps = {
  mode: 'tags',
  tokenSeparators: [',', ';', ' '],
  open: false,
  suffixIcon: null,
};

// Stable id of a service: items point at it, so renaming keeps them.
const newServiceKey = () =>
  `svc_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const newGroupKey = () =>
  `grp_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const sameName = (a, b) =>
  String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// The form holds each group with its items inside. An empty service key
// shows as "pick a service" (a group's) or "the group's service" (an item's).
const groupsToForm = (groups = [], items = []) =>
  groups.map((group) => ({
    key: group.key,
    name: group.name,
    serviceKey: group.serviceKey || undefined,
    mode: group.mode || 'multi',
    yesIsItem: group.yesIsItem !== false,
    implicit: !!group.implicit,
    items: items
      .filter((item) => item.groupKey === group.key)
      .map((item) => ({
        name: item.name,
        serviceKey: item.serviceKey || undefined,
        withDetail: !!item.withDetail,
      })),
  }));

const toFormValues = (data) => ({
  enabled: !!data.enabled,
  services: data.services || [],
  groups: groupsToForm(data.groups, data.items),
  otherItemsServiceKey: data.otherItemsServiceKey || undefined,
  mailRecipients: mailRecipientsToForm(data.mailRecipients),
});

/**
 * The groups and items as the server takes them. A list still in its one
 * implicit group (saved before groups, never regrouped: no service of its
 * own, several items, details on every item) is sent flat, so its requests
 * and emails stay as they were.
 */
const groupsPayload = (groups = []) => {
  const named = groups.filter((group) => group?.name?.trim());
  const [only] = named;
  if (
    named.length === 1 &&
    only.implicit &&
    !only.serviceKey &&
    only.mode === 'multi' &&
    (only.items || []).every((item) => !item?.name?.trim() || item.withDetail)
  ) {
    return {
      items: (only.items || [])
        .filter((item) => item?.name?.trim())
        .map((item) => ({ name: item.name.trim(), serviceKey: item.serviceKey || '' })),
    };
  }
  return {
    groups: named.map((group) => ({
      key: group.key,
      name: group.name.trim(),
      serviceKey: group.serviceKey || '',
      mode: group.mode || 'multi',
      yesIsItem: group.yesIsItem !== false,
    })),
    items: named.flatMap((group) =>
      group.mode === 'yesNo'
        ? []
        : (group.items || [])
            .filter((item) => item?.name?.trim())
            .map((item) => ({
              name: item.name.trim(),
              groupKey: group.key,
              serviceKey: item.serviceKey || '',
              withDetail: !!item.withDetail,
            }))
    ),
  };
};

/**
 * HR tile of the company profile: whether a new employee's sector heads may
 * ask for equipment and access; the services that receive the requests (IT,
 * security…) with their addresses, entered once per service; the groups of
 * the request form (Software, Shared mailboxes…), each with its service and
 * how it is filled in — several items, one, or a YES / NO question — and its
 * items, each optionally asking for details or going to a service of its
 * own; the whole list can come from an Excel sheet; and the service for
 * items the sector head types in. Saves on its own.
 */
export default function EquipmentSettingsCard({ companyId, readOnly = false }) {
  const { t, tn } = useEmployeeNoticesT();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const enabled = Form.useWatch('enabled', form);
  const services = Form.useWatch('services', form) || [];
  const groupsWatched = Form.useWatch('groups', form) || [];
  const [importOpen, setImportOpen] = useState(false);
  const totalItems = groupsWatched.reduce((sum, group) => sum + (group?.items?.length || 0), 0);

  const { data } = useQuery({
    queryKey: [EQUIPMENT_SETTINGS_QUERY_KEY, companyId],
    queryFn: () => getEquipmentSettings(companyId),
    enabled: !!companyId,
    retry: false,
  });

  useEffect(() => {
    if (data) form.setFieldsValue(toFormValues(data));
  }, [data, form]);

  const save = useMutation({
    mutationFn: (values) =>
      updateEquipmentSettings({
        company: companyId,
        enabled: !!values.enabled,
        services: (values.services || []).map((service) => ({
          key: service.key,
          name: String(service.name || '').trim(),
          emails: service.emails || [],
        })),
        ...groupsPayload(values.groups),
        otherItemsServiceKey: values.otherItemsServiceKey || '',
        mailRecipients: values.mailRecipients,
      }),
    onSuccess: (result) => {
      message.success(result?.message || tn('settingsSaved'));
      queryClient.invalidateQueries({ queryKey: [EQUIPMENT_SETTINGS_QUERY_KEY, companyId] });
      form.resetFields();
      form.setFieldsValue(toFormValues(result));
    },
    onError: (error) => message.error(errorText(error, tn('saveFailed'))),
  });

  // Hidden lists (switched off) still go out as they are: read the whole store.
  const onFinish = () => {
    const values = form.getFieldsValue(true);
    if (values.enabled && !(values.services || []).length) {
      message.error(tn('servicesRequired'));
      return;
    }
    // Not picked: typed-in items go to the first service, rather than
    // holding the save back.
    const otherItemsServiceKey =
      values.otherItemsServiceKey || serviceOptions[0]?.value;
    if (otherItemsServiceKey !== values.otherItemsServiceKey) {
      form.setFieldValue('otherItemsServiceKey', otherItemsServiceKey);
    }
    save.mutate({ ...values, otherItemsServiceKey });
  };

  const serviceOptions = services
    .filter((service) => service?.key && service?.name?.trim())
    .map((service) => ({ value: service.key, label: service.name.trim() }));

  // A removed service leaves its groups, items and typed-in items without one.
  const removeService = (remove, index) => {
    const key = form.getFieldValue(['services', index, 'key']);
    remove(index);
    const clear = (row) => (row?.serviceKey === key ? { ...row, serviceKey: undefined } : row);
    form.setFieldsValue({
      groups: (form.getFieldValue('groups') || []).map((group) => ({
        ...clear(group),
        items: (group?.items || []).map(clear),
      })),
      ...(form.getFieldValue('otherItemsServiceKey') === key
        ? { otherItemsServiceKey: undefined }
        : {}),
    });
  };

  const emailsRule = {
    validator: (_, value) => {
      if (!value?.length) return Promise.reject(new Error(tn('serviceEmailsRequired')));
      const bad = value.find((email) => !EMAIL_PATTERN.test(String(email).trim()));
      if (bad) return Promise.reject(new Error(tn('invalidRecipient', { email: bad })));
      if (value.length > MAX_EMAILS) {
        return Promise.reject(new Error(tn('tooManyRecipients', { max: MAX_EMAILS })));
      }
      return Promise.resolve();
    },
  };

  // Unique among the rows of a list (case-insensitive).
  const uniqueNameRule = (listPath, index, messageKey) => ({
    validator: (_, value) =>
      (form.getFieldValue(listPath) || []).some(
        (row, i) => i !== index && value?.trim() && sameName(row?.name, value)
      )
        ? Promise.reject(new Error(tn(messageKey)))
        : Promise.resolve(),
  });

  // An item needs a service: its own, or its group's.
  const itemServiceRule = (groupIndex) => ({
    validator: (_, value) =>
      value || form.getFieldValue(['groups', groupIndex, 'serviceKey'])
        ? Promise.resolve()
        : Promise.reject(new Error(tn('itemServiceRequired'))),
  });

  // An item's empty service shows where it goes: "IT (služba grupe)".
  const groupServicePlaceholder = (groupIndex) => {
    const key = groupsWatched[groupIndex]?.serviceKey;
    const name = serviceOptions.find((option) => option.value === key)?.label;
    return name ? tn('itemGroupServiceNamed', { service: name }) : tn('itemGroupService');
  };

  // The Excel import replaces the groups and items in the form; "Sačuvaj" saves.
  const applyImport = (plan) => {
    form.setFieldValue('groups', groupsToForm(plan.groups, plan.items));
    message.info(tn('equipmentImport.appliedSaveHint'));
  };

  // Refused (a role without these settings): nothing to show.
  if (!data) return null;

  const guideSteps = tn('equipmentGuide.steps', { returnObjects: true });
  const showSave = !readOnly && (enabled || data.enabled);

  return (
    <section className="mt-3 grid gap-2 rounded-xl border border-zinc-200 bg-white p-3">
      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        onFinish={onFinish}
        autoComplete="off"
      >
        <div className="flex items-center gap-2">
          <LaptopOutlined className="text-zinc-500" />
          <h3 className="m-0 text-[13px] font-bold text-zinc-900">{tn('equipmentSettingsTitle')}</h3>
          <Form.Item name="enabled" valuePropName="checked" noStyle>
            <Switch size="small" className="!ml-auto" />
          </Form.Item>
        </div>

        {enabled && (
          <div className="mt-3">
            <HowToCard
              title={tn('equipmentGuide.title')}
              open={!data.services?.length}
              sections={[
                {
                  title: tn('equipmentGuide.sectionTitle'),
                  steps: (Array.isArray(guideSteps) ? guideSteps : []).map((step, i) => ({
                    ...step,
                    icon: GUIDE_ICONS[i],
                  })),
                },
              ]}
            />

            <div className="mb-1.5 text-[12.5px] font-bold text-zinc-700">
              {tn('servicesTitle')}
            </div>
            <Form.List name="services">
              {(fields, { add, remove }) => (
                <div className="grid gap-1.5">
                  {fields.length > 0 && (
                    <div className={HEAD}>
                      <span>{tn('serviceName')}</span>
                      <span>{tn('serviceEmails')}</span>
                    </div>
                  )}
                  {fields.map((field) => (
                    <div key={field.key} className={ROW}>
                      <Form.Item name={[field.name, 'key']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'name']}
                        className="!mb-0"
                        rules={[
                          { required: true, whitespace: true, message: tn('serviceNameRequired') },
                          uniqueNameRule('services', field.name, 'serviceNameDuplicate'),
                        ]}
                      >
                        <Input
                          size="small"
                          placeholder={tn('serviceNamePlaceholder')}
                          maxLength={100}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'emails']} className="!mb-0" rules={[emailsRule]}>
                        <Select
                          {...emailsSelectProps}
                          size="small"
                          placeholder={tn('serviceEmailsPlaceholder')}
                        />
                      </Form.Item>
                      {!readOnly && (
                        <Button
                          size="small"
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeService(remove, field.name)}
                        />
                      )}
                    </div>
                  ))}
                  {!readOnly && (
                    <div>
                      <Button
                        size="small"
                        className="!rounded-[50px]"
                        icon={<PlusOutlined />}
                        disabled={fields.length >= MAX_SERVICES}
                        onClick={() => add({ key: newServiceKey(), name: '', emails: [] })}
                      >
                        {tn('addService')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Form.List>

            <div className="mb-1.5 mt-4 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12.5px] font-bold text-zinc-700">{tn('groupsTitle')}</span>
              {!readOnly && (
                <Button
                  size="small"
                  className="!rounded-[50px]"
                  icon={<FileExcelOutlined />}
                  onClick={() => setImportOpen(true)}
                >
                  {tn('equipmentImport.open')}
                </Button>
              )}
            </div>
            <Form.List name="groups">
              {(groupFields, { add: addGroup, remove: removeGroup, move: moveGroup }) => (
                <div className="grid gap-2">
                  {groupFields.map((groupField, groupIndex) => {
                    const mode = groupsWatched[groupField.name]?.mode || 'multi';
                    return (
                      <div
                        key={groupField.key}
                        className="grid gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 p-2.5"
                      >
                        <Form.Item name={[groupField.name, 'key']} hidden>
                          <Input />
                        </Form.Item>
                        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,160px)_minmax(0,140px)_auto] items-start gap-2">
                          <Form.Item
                            name={[groupField.name, 'name']}
                            className="!mb-0"
                            rules={[
                              { required: true, whitespace: true, message: tn('groupNameRequired') },
                              uniqueNameRule('groups', groupField.name, 'groupNameDuplicate'),
                            ]}
                          >
                            <Input
                              size="small"
                              placeholder={tn('groupNamePlaceholder')}
                              maxLength={100}
                              className="!font-semibold"
                            />
                          </Form.Item>
                          <Form.Item
                            name={[groupField.name, 'serviceKey']}
                            className="!mb-0"
                            rules={
                              mode === 'yesNo'
                                ? [{ required: true, message: tn('groupServiceRequired') }]
                                : []
                            }
                          >
                            <Select
                              size="small"
                              allowClear
                              options={serviceOptions}
                              placeholder={tn('groupServicePlaceholder')}
                              notFoundContent={tn('noServicesYet')}
                            />
                          </Form.Item>
                          <Form.Item name={[groupField.name, 'mode']} className="!mb-0">
                            <Select
                              size="small"
                              options={MODES.map((value) => ({ value, label: tn(MODE_KEYS[value]) }))}
                            />
                          </Form.Item>
                          {!readOnly && (
                            <span className="flex">
                              <Button
                                size="small"
                                type="text"
                                icon={<ArrowUpOutlined />}
                                disabled={groupIndex === 0}
                                onClick={() => moveGroup(groupIndex, groupIndex - 1)}
                              />
                              <Button
                                size="small"
                                type="text"
                                icon={<ArrowDownOutlined />}
                                disabled={groupIndex === groupFields.length - 1}
                                onClick={() => moveGroup(groupIndex, groupIndex + 1)}
                              />
                              <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeGroup(groupField.name)}
                              />
                            </span>
                          )}
                        </div>

                        {mode === 'yesNo' ? (
                          <div className="flex items-center gap-2 text-[12px] text-zinc-600">
                            <Form.Item
                              name={[groupField.name, 'yesIsItem']}
                              valuePropName="checked"
                              noStyle
                            >
                              <Switch size="small" />
                            </Form.Item>
                            <span>{tn('yesIsItemLabel')}</span>
                          </div>
                        ) : (
                          <Form.List name={[groupField.name, 'items']}>
                            {(fields, { add, remove }) => (
                              <div className="grid gap-1.5 pl-2">
                                {fields.length > 0 && (
                                  <div className={`${ITEM_ROW} text-[11.5px] font-semibold text-zinc-500`}>
                                    <span>{tn('extraNamePlaceholder')}</span>
                                    <span>{tn('itemOwnService')}</span>
                                    <span>{tn('itemDetails')}</span>
                                  </div>
                                )}
                                {fields.map((field) => (
                                  <div key={field.key} className={ITEM_ROW}>
                                    <Form.Item
                                      name={[field.name, 'name']}
                                      className="!mb-0"
                                      rules={[
                                        { required: true, whitespace: true, message: tn('itemNameRequired') },
                                        uniqueNameRule(
                                          ['groups', groupField.name, 'items'],
                                          field.name,
                                          'itemNameDuplicate'
                                        ),
                                      ]}
                                    >
                                      <Input
                                        size="small"
                                        placeholder={tn('extraNamePlaceholder')}
                                        maxLength={100}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      name={[field.name, 'serviceKey']}
                                      className="!mb-0"
                                      dependencies={[['groups', groupField.name, 'serviceKey']]}
                                      rules={[itemServiceRule(groupField.name)]}
                                    >
                                      <Select
                                        size="small"
                                        allowClear
                                        options={serviceOptions}
                                        placeholder={groupServicePlaceholder(groupField.name)}
                                        notFoundContent={tn('noServicesYet')}
                                      />
                                    </Form.Item>
                                    <Tooltip title={tn('itemDetailsHint')}>
                                      <Form.Item
                                        name={[field.name, 'withDetail']}
                                        valuePropName="checked"
                                        className="!mb-0"
                                      >
                                        <Switch size="small" />
                                      </Form.Item>
                                    </Tooltip>
                                    {!readOnly && (
                                      <Button
                                        size="small"
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(field.name)}
                                      />
                                    )}
                                  </div>
                                ))}
                                {!readOnly && (
                                  <div>
                                    <Button
                                      size="small"
                                      className="!rounded-[50px]"
                                      icon={<PlusOutlined />}
                                      disabled={totalItems >= MAX_ITEMS}
                                      onClick={() =>
                                        add({ name: '', serviceKey: undefined, withDetail: false })
                                      }
                                    >
                                      {tn('addExtra')}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </Form.List>
                        )}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <div>
                      <Button
                        size="small"
                        className="!rounded-[50px]"
                        icon={<PlusOutlined />}
                        disabled={groupFields.length >= MAX_GROUPS}
                        onClick={() =>
                          addGroup({
                            key: newGroupKey(),
                            name: '',
                            serviceKey:
                              serviceOptions.length === 1 ? serviceOptions[0].value : undefined,
                            mode: 'multi',
                            yesIsItem: true,
                            implicit: false,
                            items: [],
                          })
                        }
                      >
                        {tn('addGroup')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Form.List>

            <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[12.5px] text-zinc-700">{tn('otherItemsService')}</span>
              <Form.Item name="otherItemsServiceKey" className="!mb-0 w-full max-w-[240px]">
                <Select
                  size="small"
                  options={serviceOptions}
                  placeholder={tn('pickService')}
                  notFoundContent={tn('noServicesYet')}
                />
              </Form.Item>
            </div>

            <MailRecipientsField companyId={companyId} hint={tn('mailRecipients.hintEquipment')} />
          </div>
        )}

        <EquipmentImportModal
          companyId={companyId}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          services={serviceOptions.map((option) => ({ key: option.value, name: option.label }))}
          onApply={applyImport}
        />

        {showSave && (
          <div className="mt-3 flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              className="!rounded-[50px]"
              loading={save.isPending}
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        )}
      </Form>
    </section>
  );
}
